import React, { useState, useEffect } from 'react';
import { Route, RefreshCw, Plus, Pencil, Eye, X, CheckCircle, AlertTriangle, XCircle, Gauge, BarChart3, Settings, Play, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };
const inputClass = "w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors";
const inputBorder = { border: '1px solid rgba(99, 102, 241, 0.15)' };
const labelClass = "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5";

const RoutingIntelligence = () => {
  const [tabValue, setTabValue] = useState(0);
  const [routes, setRoutes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [activePolicy, setActivePolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyWeights, setPolicyWeights] = useState({ sla_weight: 0.40, health_weight: 0.30, cost_weight: 0.20, priority_weight: 0.10 });
  const [routeDetailsOpen, setRouteDetailsOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [simulationResults, setSimulationResults] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch real connector health data from live-binds endpoint
      const healthRes = await api.get('/admin/smpp/live-binds/health');
      if (healthRes.data?.success && healthRes.data?.data) {
        const mapped = healthRes.data.data.map(h => ({
          id: h.connector_id,
          route_name: h.connector_name,
          operator_name: h.connector_name,
          channel_type: 'SMPP',
          health_score: h.health_score || 0,
          health_status: h.health_status || 'UNKNOWN',
          success_rate: h.messages_sent + h.messages_failed > 0 ? (h.messages_sent / (h.messages_sent + h.messages_failed) * 100) : 0,
          avg_latency_ms: 0,
          current_tps: 0,
          tps_limit: 0,
          is_active: h.is_active,
          final_score: h.health_score || 0,
        }));
        setRoutes(mapped);
      }
      // Fetch policies (graceful — may not exist)
      try {
        const [policiesRes, activeRes] = await Promise.all([
          api.get('/admin/routing/policies'),
          api.get('/admin/routing/policies/active')
        ]);
        if (policiesRes.data?.data) setPolicies(policiesRes.data.data);
        if (activeRes.data?.data) setActivePolicy(activeRes.data.data);
      } catch (e) { /* policies endpoints may not exist yet */ }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch routing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyWeights({ sla_weight: policy.sla_weight, health_weight: policy.health_weight, cost_weight: policy.cost_weight, priority_weight: policy.priority_weight });
    setPolicyDialogOpen(true);
  };

  const handleCreatePolicy = () => {
    setEditingPolicy(null);
    setPolicyWeights({ sla_weight: 0.40, health_weight: 0.30, cost_weight: 0.20, priority_weight: 0.10 });
    setPolicyDialogOpen(true);
  };

  const handleSavePolicy = async () => {
    try {
      const policyData = { ...policyWeights, policy_name: editingPolicy?.policy_name || `policy_${Date.now()}`, description: editingPolicy?.description || 'Custom routing policy' };
      if (editingPolicy) {
        await api.put(`/admin/routing/policies/${editingPolicy.policy_name}`, policyData);
      } else {
        await api.post('/admin/routing/policies', policyData);
      }
      setPolicyDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save policy:', error);
    }
  };

  const handleActivatePolicy = async (policyName) => {
    try {
      await api.post(`/admin/routing/policies/${policyName}/activate`, { reason: 'Manual activation from admin panel' });
      fetchData();
    } catch (error) {
      console.error('Failed to activate policy:', error);
    }
  };

  const handleSimulatePolicy = async (policy) => {
    try {
      setSimulating(true);
      const response = await api.post(`/admin/routing/policies/${policy.policy_name}/simulate`, { sample_size: 1000 });
      setSimulationResults(response.data.data);
    } catch (error) {
      console.error('Failed to simulate policy:', error);
    } finally {
      setSimulating(false);
    }
  };

  const handleViewRouteDetails = (route) => {
    setSelectedRoute(route);
    setRouteDetailsOpen(true);
  };

  const getHealthStyle = (score) => {
    if (score >= 90) return { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' };
    if (score >= 70) return { background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)' };
    return { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' };
  };

  const getHealthBarColor = (score) => {
    if (score >= 90) return '#059669';
    if (score >= 70) return '#d97706';
    return '#dc2626';
  };

  const formatWeight = (value) => `${(value * 100).toFixed(0)}%`;
  const totalWeight = Object.values(policyWeights).reduce((a, b) => a + b, 0);

  const tabs = [
    { label: 'Route Health', icon: Gauge },
    { label: 'Policies', icon: Settings },
    { label: 'Analytics', icon: BarChart3 },
  ];

  const avgHealth = routes.length > 0 ? (routes.reduce((sum, r) => sum + (r.health_score || 0), 0) / routes.length).toFixed(1) : 0;

  const statCards = [
    { label: 'Active Routes', value: routes.filter(r => r.is_active).length, color: '#059669', bg: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.12)' },
    { label: 'Avg Health', value: `${avgHealth}%`, color: '#059669', bg: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.12)' },
    { label: 'Active Policy', value: activePolicy?.policy_name || 'None', color: '#4f46e5', bg: 'rgba(99, 102, 241, 0.06)', borderColor: 'rgba(99, 102, 241, 0.12)', small: true },
    { label: 'Decision Latency', value: '<10ms', color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.06)', borderColor: 'rgba(6, 182, 212, 0.12)' },
  ];

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Route className="w-5 h-5 text-white" />
            </div>
            Intelligent Routing
          </h1>
          <p className="text-sm text-gray-500 mt-1">Route health monitoring and policy management</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : ''}</span>
          <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm" style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statCards.map((card, i) => (
          <div key={i} className="bg-white rounded-xl p-4" style={{ ...cardStyle, background: card.bg, borderColor: card.borderColor }}>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{card.label}</p>
            <span className={`${card.small ? 'text-sm' : 'text-2xl'} font-bold`} style={{ color: card.color }}>{card.value}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <div className="flex gap-0 px-2 pt-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          {tabs.map((tab, i) => {
            const Icon = tab.icon;
            return (
              <button key={i} onClick={() => setTabValue(i)} className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors ${tabValue === i ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`} style={tabValue === i ? { background: 'rgba(99, 102, 241, 0.06)', borderBottom: '2px solid #4f46e5' } : {}}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {/* Route Health Tab */}
          {tabValue === 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Route</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Operator</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Channel</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Health</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Success</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Latency</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">TPS</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {routes.length === 0 ? (
                    <tr><td colSpan="9" className="px-6 py-12 text-center"><Route className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No routes found</p></td></tr>
                  ) : routes.map((route) => (
                    <tr key={route.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-800">{route.route_name}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{route.operator_name}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' }}>{route.channel_type}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${route.health_score || 0}%`, background: getHealthBarColor(route.health_score) }} /></div>
                          <span className="text-xs font-semibold text-gray-700">{route.health_score?.toFixed(1) || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{route.success_rate?.toFixed(1) || 0}%</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{route.avg_latency_ms || 0}ms</td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-600">{route.current_tps || 0}/{route.tps_limit || 0}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getHealthStyle(route.health_score)}>{route.health_status || 'UNKNOWN'}</span></td>
                      <td className="px-4 py-3"><button onClick={() => handleViewRouteDetails(route)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="Details"><Eye className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Policies Tab */}
          {tabValue === 1 && (
            <div>
              <div className="mb-4">
                <button onClick={handleCreatePolicy} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  <Plus className="w-3.5 h-3.5" /> Create Policy
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {policies.map((policy) => (
                  <div key={policy.policy_name} className="bg-white rounded-xl p-5 relative" style={cardStyle}>
                    {policy.is_active && (
                      <span className="absolute top-4 right-4 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>ACTIVE</span>
                    )}
                    <h4 className="text-sm font-bold text-gray-800 mb-1">{policy.policy_name}</h4>
                    <p className="text-[10px] text-gray-400 mb-3">{policy.description}</p>
                    <div className="space-y-2" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)', paddingTop: '0.75rem' }}>
                      {[
                        ['SLA', policy.sla_weight, '#4f46e5'],
                        ['Health', policy.health_weight, '#059669'],
                        ['Cost', policy.cost_weight, '#d97706'],
                        ['Priority', policy.priority_weight, '#06b6d4'],
                      ].map(([name, weight, color]) => (
                        <div key={name}>
                          <div className="flex justify-between text-[10px] mb-0.5"><span className="text-gray-400">{name}</span><span className="font-semibold" style={{ color }}>{formatWeight(weight)}</span></div>
                          <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${weight * 100}%`, background: color }} /></div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-1.5 mt-3 pt-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                      <button onClick={() => handleEditPolicy(policy)} className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium" style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}>Edit</button>
                      {!policy.is_active && <button onClick={() => handleActivatePolicy(policy.policy_name)} className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium" style={{ color: '#059669', background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.12)' }}>Activate</button>}
                      <button onClick={() => handleSimulatePolicy(policy)} disabled={simulating} className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium" style={{ color: '#6b7280', background: 'rgba(107, 114, 128, 0.06)', border: '1px solid rgba(107, 114, 128, 0.12)' }}>Simulate</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {tabValue === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <h4 className="text-sm font-bold text-gray-800 mb-4">Route Score Distribution</h4>
                <div className="space-y-3">
                  {routes.map((route) => (
                    <div key={route.id}>
                      <div className="flex justify-between text-[10px] mb-0.5"><span className="text-gray-600">{route.route_name}</span><span className="font-semibold text-gray-800">{route.final_score?.toFixed(1) || 0}</span></div>
                      <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${route.final_score || 0}%`, background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }} /></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <h4 className="text-sm font-bold text-gray-800 mb-4">Cost vs SLA Analysis</h4>
                <div className="rounded-lg p-3" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#6366f1' }} />
                    <p className="text-xs text-gray-600">Current policy balances cost efficiency with delivery SLA. SLA weight: {formatWeight(activePolicy?.sla_weight || 0.40)}, Cost weight: {formatWeight(activePolicy?.cost_weight || 0.20)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Policy Edit Modal */}
      {policyDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>{editingPolicy ? <Pencil className="w-3.5 h-3.5 text-white" /> : <Plus className="w-3.5 h-3.5 text-white" />}</div>
                {editingPolicy ? 'Edit Policy' : 'Create Policy'}
              </h3>
              <button onClick={() => setPolicyDialogOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className={labelClass}>Policy Name</label><input className={inputClass} style={inputBorder} defaultValue={editingPolicy?.policy_name || ''} disabled={!!editingPolicy} /></div>
              <div><label className={labelClass}>Description</label><textarea className="w-full px-3 py-2 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" style={inputBorder} rows={2} defaultValue={editingPolicy?.description || ''} /></div>
              {[
                ['SLA Weight', 'sla_weight', '#4f46e5'],
                ['Health Weight', 'health_weight', '#059669'],
                ['Cost Weight', 'cost_weight', '#d97706'],
                ['Priority Weight', 'priority_weight', '#06b6d4'],
              ].map(([label, key, color]) => (
                <div key={key}>
                  <div className="flex justify-between mb-1"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</span><span className="text-xs font-semibold" style={{ color }}>{formatWeight(policyWeights[key])}</span></div>
                  <input type="range" className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{ accentColor: color }} min={0} max={1} step={0.05} value={policyWeights[key]} onChange={(e) => setPolicyWeights({...policyWeights, [key]: parseFloat(e.target.value)})} />
                </div>
              ))}
              <div className="rounded-lg p-3 flex items-center gap-2" style={Math.abs(totalWeight - 1.0) < 0.01 ? { background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' } : { background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                {Math.abs(totalWeight - 1.0) < 0.01 ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <span className="text-xs font-medium" style={{ color: Math.abs(totalWeight - 1.0) < 0.01 ? '#059669' : '#d97706' }}>Total: {(totalWeight * 100).toFixed(0)}%{Math.abs(totalWeight - 1.0) >= 0.01 && ' (Must equal 100%)'}</span>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setPolicyDialogOpen(false)} className="px-4 py-2 rounded-xl text-xs font-medium" style={{ color: '#6b7280', background: 'rgba(107, 114, 128, 0.06)', border: '1px solid rgba(107, 114, 128, 0.15)' }}>Cancel</button>
                <button onClick={handleSavePolicy} disabled={Math.abs(totalWeight - 1.0) >= 0.01} className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Route Details Modal */}
      {routeDetailsOpen && selectedRoute && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900">Route Details</h3>
              <button onClick={() => setRouteDetailsOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-3">
              {[
                ['Route Name', selectedRoute.route_name],
                ['Operator', selectedRoute.operator_name],
                ['Channel', selectedRoute.channel_type],
                ['Health Score', `${selectedRoute.health_score?.toFixed(1) || 0}%`],
              ].map(([k, v], i) => (
                <div key={i} className="flex justify-between py-2" style={i > 0 ? { borderTop: '1px solid rgba(99, 102, 241, 0.05)' } : {}}>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{k}</span>
                  <span className="text-xs font-mono text-gray-800">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutingIntelligence;
